import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { driftplanApi, type DriftplanGrid, type DriftplanTask } from '../services/driftplan';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('sv-SE');
}

function getIsoWeekInfo(date: Date): { isoYear: number; isoWeek: number } {
  const copy = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = copy.getUTCDay() || 7;
  copy.setUTCDate(copy.getUTCDate() + 4 - day);
  const isoYear = copy.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const isoWeek = Math.ceil((((copy.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { isoYear, isoWeek };
}

export function DriftplanPage() {
  const { year: yearParam } = useParams<{ year?: string }>();
  const navigate = useNavigate();
  const { error: toastError, success: toastSuccess } = useToast();
  const { canWrite } = useAuth();

  const startYear = useMemo(() => {
    const parsed = Number(yearParam);
    if (Number.isInteger(parsed) && parsed >= 2000 && parsed <= 2100) return parsed;
    return new Date().getFullYear();
  }, [yearParam]);

  const [year, setYear] = useState<number>(startYear);
  const [years, setYears] = useState<number[]>([]);
  const [grid, setGrid] = useState<DriftplanGrid | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [editMode, setEditMode] = useState<boolean>(false);
  const [draftValue, setDraftValue] = useState<string>('');
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [cellDialog, setCellDialog] = useState<{ weekNumber: number; columnKey: string; previousValue: string | null } | null>(null);

  const [copyOpen, setCopyOpen] = useState<boolean>(false);
  const [copyFromYear, setCopyFromYear] = useState<number>(startYear);
  const [copyToYear, setCopyToYear] = useState<number>(startYear + 1);
  const [copyColumns, setCopyColumns] = useState<Record<string, boolean>>({});
  const [copying, setCopying] = useState<boolean>(false);
  const [weekDialog, setWeekDialog] = useState<{
    weekNumber: number;
    fields: Record<string, string>;
    original: Record<string, string | null>;
  } | null>(null);
  const [savingWeekDialog, setSavingWeekDialog] = useState<boolean>(false);

  const [taskDialog, setTaskDialog] = useState<{ mode: 'create' | 'edit'; weekNumber: number; taskId?: number; name: string; dueDate: string; done: boolean } | null>(null);
  const [savingTaskDialog, setSavingTaskDialog] = useState<boolean>(false);

  const [movingTask, setMovingTask] = useState<{ taskId: number; toWeek: number } | null>(null);
  const [movingBusy, setMovingBusy] = useState<boolean>(false);

  const writable = canWrite('driftplan');
  const isoNow = useMemo(() => getIsoWeekInfo(new Date()), []);
  const toastErrorRef = useRef(toastError);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const autoScrolledYearRef = useRef<number | null>(null);

  useEffect(() => {
    toastErrorRef.current = toastError;
  }, [toastError]);

  useEffect(() => {
    setYear(startYear);
  }, [startYear]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const [gridRes, yearsRes] = await Promise.all([
          driftplanApi.getGrid(year),
          driftplanApi.listYears(),
        ]);
        if (cancelled) return;
        setGrid(gridRes);
        setYears(yearsRes.years.length > 0 ? yearsRes.years : [year]);
      } catch (e) {
        if (!cancelled) {
          toastErrorRef.current(e instanceof Error ? e.message : 'Kunde inte ladda driftplan');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [year]);

  const scrollToCurrentWeek = () => {
    const host = tableScrollRef.current;
    if (!host || !grid) return;
    if (grid.plan.year !== isoNow.isoYear) return;
    const row = host.querySelector<HTMLTableRowElement>(`tr[data-week="${isoNow.isoWeek}"]`);
    if (!row) return;
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  useEffect(() => {
    if (!grid) return;
    if (grid.plan.year !== isoNow.isoYear) return;
    if (autoScrolledYearRef.current === grid.plan.year) return;
    autoScrolledYearRef.current = grid.plan.year;
    window.setTimeout(() => {
      scrollToCurrentWeek();
    }, 0);
  }, [grid, isoNow.isoWeek, isoNow.isoYear]);

  const goToYear = (targetYear: number) => {
    setCellDialog(null);
    navigate(`/driftplan/${targetYear}`);
  };

  const startEdit = (weekNumber: number, columnKey: string, currentValue: string | null) => {
    if (!editMode || !writable) return;
    if (savingCell) return;
    setCellDialog({ weekNumber, columnKey, previousValue: currentValue });
    setDraftValue(currentValue ?? '');
  };

  const saveCell = async (weekNumber: number, columnKey: string, previousValue: string | null) => {
    if (!grid || !writable) return;
    const key = `${weekNumber}:${columnKey}`;
    setCellDialog(null);
    const prev = (previousValue ?? '').trim();
    const next = draftValue.trim();
    if (prev === next) return;
    setSavingCell(key);
    try {
      await driftplanApi.setCell({ year: grid.plan.year, weekNumber, columnKey, value: draftValue });
      setGrid((prevGrid) => {
        if (!prevGrid) return prevGrid;
        return {
          ...prevGrid,
          rows: prevGrid.rows.map((r) =>
            r.weekNumber === weekNumber
              ? { ...r, values: { ...r.values, [columnKey]: next || null } }
              : r
          ),
        };
      });
    } catch (e) {
      toastError(e instanceof Error ? e.message : 'Kunde inte spara cell');
    } finally {
      setSavingCell(null);
    }
  };

  const openCopyDialog = () => {
    if (!grid) return;
    const selectable = grid.columns.filter((c) => c.columnType === 'TEXT' || c.key === 'tasks');
    const initial: Record<string, boolean> = {};
    for (const c of selectable) initial[c.key] = true;
    setCopyColumns(initial);
    setCopyFromYear(grid.plan.year);
    setCopyToYear(grid.plan.year + 1);
    setCopyOpen(true);
  };

  const openWeekDialog = (weekNumber: number, values: Record<string, string | null>) => {
    if (!grid || !writable) return;
    const textColumns = grid.columns.filter((c) => c.columnType === 'TEXT');
    const fields: Record<string, string> = {};
    const original: Record<string, string | null> = {};
    for (const col of textColumns) {
      fields[col.key] = values[col.key] ?? '';
      original[col.key] = values[col.key] ?? null;
    }
    setWeekDialog({ weekNumber, fields, original });
  };

  const saveWeekDialog = async () => {
    if (!grid || !weekDialog || !writable) return;
    const changedEntries = Object.entries(weekDialog.fields).filter(([key, value]) => {
      const prev = (weekDialog.original[key] ?? '').trim();
      const next = value.trim();
      return prev !== next;
    });
    if (changedEntries.length === 0) {
      setWeekDialog(null);
      return;
    }
    setSavingWeekDialog(true);
    try {
      await Promise.all(
        changedEntries.map(([columnKey, value]) =>
          driftplanApi.setCell({
            year: grid.plan.year,
            weekNumber: weekDialog.weekNumber,
            columnKey,
            value,
          })
        )
      );

      setGrid((prevGrid) => {
        if (!prevGrid) return prevGrid;
        return {
          ...prevGrid,
          rows: prevGrid.rows.map((row) =>
            row.weekNumber === weekDialog.weekNumber
              ? {
                  ...row,
                  values: {
                    ...row.values,
                    ...Object.fromEntries(
                      changedEntries.map(([k, v]) => [k, v.trim() === '' ? null : v.trim()])
                    ),
                  },
                }
              : row
          ),
        };
      });
      setWeekDialog(null);
      toastSuccess('Veckan sparades');
    } catch (e) {
      toastError(e instanceof Error ? e.message : 'Kunde inte spara veckans fält');
    } finally {
      setSavingWeekDialog(false);
    }
  };

  const handleCopyYear = async () => {
    const selected = Object.entries(copyColumns).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) {
      toastError('Välj minst en kolumn att kopiera');
      return;
    }
    setCopying(true);
    try {
      await driftplanApi.copyYear({ fromYear: copyFromYear, toYear: copyToYear, columnKeys: selected });
      toastSuccess('Året kopierades');
      setCopyOpen(false);
      goToYear(copyToYear);
    } catch (e) {
      toastError(e instanceof Error ? e.message : 'Kunde inte kopiera år');
    } finally {
      setCopying(false);
    }
  };

  const openCreateTaskDialog = (week: number) => {
    setTaskDialog({ mode: 'create', weekNumber: week, name: '', dueDate: '', done: false });
  };

  const openEditTaskDialog = (week: number, task: DriftplanTask) => {
    setTaskDialog({
      mode: 'edit',
      weekNumber: week,
      taskId: task.id,
      name: task.name,
      dueDate: task.dueDate ?? '',
      done: task.done,
    });
  };

  const saveTaskDialog = async () => {
    if (!grid || !writable) return;
    if (!taskDialog) return;
    if (!taskDialog.name.trim()) {
      toastError('Ärendenamn krävs');
      return;
    }
    setSavingTaskDialog(true);
    try {
      if (taskDialog.mode === 'create') {
        const created = await driftplanApi.createTask({
          year: grid.plan.year,
          weekNumber: taskDialog.weekNumber,
          name: taskDialog.name.trim(),
          dueDate: taskDialog.dueDate || null,
        });
        setGrid((prevGrid) => {
          if (!prevGrid) return prevGrid;
          return {
            ...prevGrid,
            rows: prevGrid.rows.map((r) =>
              r.weekNumber === taskDialog.weekNumber
                ? {
                    ...r,
                    tasks: [
                      ...r.tasks,
                      {
                        id: created.id,
                        name: taskDialog.name.trim(),
                        dueDate: taskDialog.dueDate || null,
                        done: false,
                        doneDate: null,
                        sortOrder: (r.tasks.length > 0 ? r.tasks[r.tasks.length - 1].sortOrder : 0) + 10,
                      },
                    ],
                  }
                : r
            ),
          };
        });
      } else if (taskDialog.taskId) {
        await driftplanApi.updateTask(taskDialog.taskId, {
          name: taskDialog.name.trim(),
          dueDate: taskDialog.dueDate || null,
        });
        updateTaskInGrid(taskDialog.taskId, (task) => ({
          ...task,
          name: taskDialog.name.trim(),
          dueDate: taskDialog.dueDate || null,
        }));
      }
      setTaskDialog(null);
    } catch (e) {
      toastError(e instanceof Error ? e.message : 'Kunde inte skapa ärende');
    } finally {
      setSavingTaskDialog(false);
    }
  };

  const updateTaskInGrid = (taskId: number, updater: (task: DriftplanTask) => DriftplanTask) => {
    setGrid((prevGrid) => {
      if (!prevGrid) return prevGrid;
      return {
        ...prevGrid,
        rows: prevGrid.rows.map((row) => ({
          ...row,
          tasks: row.tasks.map((t) => (t.id === taskId ? updater(t) : t)),
        })),
      };
    });
  };

  const handleToggleTaskDone = async (taskId: number, done: boolean) => {
    try {
      await driftplanApi.updateTask(taskId, { done });
      updateTaskInGrid(taskId, (task) => ({
        ...task,
        done,
        doneDate: done ? new Date().toISOString().slice(0, 10) : null,
      }));
    } catch (e) {
      toastError(e instanceof Error ? e.message : 'Kunde inte uppdatera ärende');
    }
  };

  const moveTask = async () => {
    if (!movingTask) return;
    if (movingTask.toWeek < 1 || movingTask.toWeek > 53) {
      toastError('Ogiltig vecka');
      return;
    }
    setMovingBusy(true);
    try {
      await driftplanApi.updateTask(movingTask.taskId, { weekNumber: movingTask.toWeek });
      setGrid((prevGrid) => {
        if (!prevGrid) return prevGrid;
        let moving: DriftplanTask | null = null;
        const rowsWithout = prevGrid.rows.map((row) => {
          const filtered = row.tasks.filter((t) => {
            if (t.id === movingTask.taskId) {
              moving = t;
              return false;
            }
            return true;
          });
          return { ...row, tasks: filtered };
        });
        if (!moving) return prevGrid;
        return {
          ...prevGrid,
          rows: rowsWithout.map((row) =>
            row.weekNumber === movingTask.toWeek ? { ...row, tasks: [...row.tasks, moving!] } : row
          ),
        };
      });
      setMovingTask(null);
    } catch (e) {
      toastError(e instanceof Error ? e.message : 'Kunde inte flytta ärende');
    } finally {
      setMovingBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Driftplan {year}</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-secondary h-8 w-8 px-0 py-0"
            onClick={() => goToYear(year - 1)}
            title="Föregående år"
            aria-label="Föregående år"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M14.7 6.3a1 1 0 0 1 0 1.4L10.41 12l4.3 4.3a1 1 0 0 1-1.42 1.4l-5-5a1 1 0 0 1 0-1.4l5-5a1 1 0 0 1 1.41 0z" />
            </svg>
          </button>
          <select className="px-2 py-1 text-sm border border-gray-300 rounded" value={year} onChange={(e) => goToYear(Number(e.target.value))}>
            {Array.from(new Set([...years, year])).sort((a, b) => b - a).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-secondary h-8 w-8 px-0 py-0"
            onClick={() => goToYear(year + 1)}
            title="Nästa år"
            aria-label="Nästa år"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M9.3 17.7a1 1 0 0 1 0-1.4L13.59 12l-4.3-4.3a1 1 0 1 1 1.42-1.4l5 5a1 1 0 0 1 0 1.4l-5 5a1 1 0 0 1-1.41 0z" />
            </svg>
          </button>
          <button
            type="button"
            className="btn btn-secondary h-8 w-8 px-0 py-0"
            onClick={() => {
              if (year !== isoNow.isoYear) {
                goToYear(isoNow.isoYear);
                return;
              }
              scrollToCurrentWeek();
            }}
            title="Aktuellt år"
            aria-label="Aktuellt år"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1zm12 8H5v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9zM6 6a1 1 0 0 0-1 1v1h14V7a1 1 0 0 0-1-1H6z" />
            </svg>
          </button>
          <button
            type="button"
            disabled={!writable}
            onClick={openCopyDialog}
            className="btn btn-secondary h-8 w-8 px-0 py-0"
            title="Kopiera år"
            aria-label="Kopiera år"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M8 8V5a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-3v3a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v-8a3 3 0 0 1 3-3h3zm8 8H11a3 3 0 0 1-3-3V8H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-3h2zm-5-12a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-8z" />
            </svg>
          </button>
          <button
            type="button"
            disabled={!writable}
            onClick={() => setEditMode((v) => !v)}
            className={`btn h-8 w-8 px-0 py-0 ${editMode ? 'btn-success' : 'btn-primary'}`}
            title={editMode ? 'Redigering aktiv' : 'Aktivera redigering'}
            aria-label={editMode ? 'Redigering aktiv' : 'Aktivera redigering'}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M3 17.25V21h3.75l11-11-3.75-3.75-11 11zM20.71 7.04a1 1 0 0 0 0-1.41L18.37 3.3a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.84z" />
            </svg>
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-gray-500">Laddar driftplan...</p>}
      {!loading && !grid && <p className="text-sm text-gray-500">Ingen driftplan kunde laddas.</p>}

      {!loading && grid && (
        <div className="border border-gray-200 rounded overflow-hidden w-full">
          <div ref={tableScrollRef} className="overflow-auto w-full" style={{ maxHeight: 'calc(100vh - 13rem)' }}>
            <table className="w-max min-w-[860px] md:min-w-[980px] border-collapse table-fixed text-[11px] leading-tight">
              <thead className="sticky top-0 z-10 bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="sticky left-0 z-20 bg-gray-100 border-r border-gray-200 text-left px-1 py-0.5 font-medium w-[42px] min-w-[42px] max-w-[42px]">V</th>
                  <th className="text-left px-1 py-0.5 font-medium w-[76px] min-w-[76px] max-w-[76px] whitespace-nowrap">Måndag</th>
                  {grid.columns.map((col) => (
                    <th
                      key={col.key}
                      className={`text-left px-1 py-0.5 font-medium break-words ${
                        col.key === 'work_days'
                          ? 'w-[70px] min-w-[70px] max-w-[70px] whitespace-nowrap'
                          : 'w-[116px] min-w-[116px] max-w-[116px]'
                      }`}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((row) => {
                  const isCurrentWeek = grid.plan.year === isoNow.isoYear && row.weekNumber === isoNow.isoWeek;
                  return (
                    <tr key={row.weekNumber} data-week={row.weekNumber} className="border-t border-gray-100 odd:bg-white even:bg-gray-50/40">
                      <td className={`sticky left-0 z-[1] border-r border-gray-200 px-0.5 py-0.5 text-center w-[42px] min-w-[42px] max-w-[42px] ${isCurrentWeek ? 'bg-orange-100 text-orange-800' : 'bg-white'}`}>
                        <button
                          type="button"
                          className="btn h-6 w-full px-0 py-0 text-[11px] font-semibold bg-transparent text-gray-900 hover:bg-orange-50 disabled:hover:bg-transparent"
                          onClick={() => openWeekDialog(row.weekNumber, row.values)}
                          disabled={!writable}
                          title={writable ? `Redigera vecka ${row.weekNumber}` : 'Saknar skrivrättighet'}
                        >
                          {row.weekNumber}
                        </button>
                      </td>
                      <td className="px-1 py-0.5 whitespace-nowrap w-[76px] min-w-[76px] max-w-[76px]">{formatDate(row.mondayDate)}</td>
                      {grid.columns.map((col) => {
                        if (col.key === 'tasks') {
                          return (
                            <td key={col.key} className="px-0.5 py-0.5 align-top w-[116px] min-w-[116px] max-w-[116px]">
                              <div className="space-y-0.5 min-h-[32px]">
                                {row.tasks.map((task) => (
                                  <div key={task.id} className="rounded border border-gray-200 bg-white px-1 py-0.5">
                                    <div className="flex items-center gap-1">
                                      <input type="checkbox" checked={task.done} disabled={!writable} onChange={(e) => handleToggleTaskDone(task.id, e.target.checked)} />
                                      <span className={`min-w-0 flex-1 break-words ${task.done ? 'line-through text-gray-500' : ''}`}>{task.name}</span>
                                      <button
                                        type="button"
                                        disabled={!writable}
                                        onClick={() => openEditTaskDialog(row.weekNumber, task)}
                                        className="btn btn-secondary h-6 w-6 px-0 py-0"
                                        title="Redigera ärende"
                                        aria-label="Redigera ärende"
                                      >
                                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true">
                                          <path d="M3 17.25V21h3.75l11-11-3.75-3.75-11 11zM20.71 7.04a1 1 0 0 0 0-1.41L18.37 3.3a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.84z" />
                                        </svg>
                                      </button>
                                      <button
                                        type="button"
                                        disabled={!writable}
                                        onClick={() => setMovingTask({ taskId: task.id, toWeek: row.weekNumber })}
                                        className="btn btn-secondary ml-auto h-6 px-1 py-0 text-[10px]"
                                        title="Flytta ärende"
                                      >
                                        Flytta
                                      </button>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Klart datum: {formatDate(task.dueDate)} • Done: {task.done ? 'Ja' : 'Nej'} • DoneDate: {formatDate(task.doneDate)}
                                    </div>
                                  </div>
                                ))}
                                {writable && (
                                  <button
                                    type="button"
                                    onClick={() => openCreateTaskDialog(row.weekNumber)}
                                    className="btn btn-secondary h-7 w-7 px-0 py-0 text-primary"
                                    title="Nytt ärende"
                                    aria-label="Nytt ärende"
                                  >
                                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                                      <path d="M11 4a1 1 0 1 1 2 0v7h7a1 1 0 1 1 0 2h-7v7a1 1 0 1 1-2 0v-7H4a1 1 0 1 1 0-2h7V4z" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          );
                        }

                        const key = `${row.weekNumber}:${col.key}`;
                        const value = row.values[col.key] ?? '';
                        const isSaving = savingCell === key;
                        return (
                          <td
                            key={col.key}
                            className={`px-0.5 py-0.5 align-top ${col.key === 'work_days' ? 'w-[70px] min-w-[70px] max-w-[70px]' : 'w-[116px] min-w-[116px] max-w-[116px]'} ${editMode && writable ? 'cursor-text' : ''}`}
                            onClick={() => startEdit(row.weekNumber, col.key, row.values[col.key])}
                          >
                            <div className="min-h-[32px] whitespace-pre-wrap break-words text-gray-800">
                              {isSaving ? <span className="text-gray-400">Sparar...</span> : value}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {cellDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-3xl rounded border border-gray-200 bg-white p-3 space-y-3">
            <h2 className="text-base font-semibold">Redigera text • Vecka {cellDialog.weekNumber}</h2>
            <textarea
              autoFocus
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              className="w-full min-h-[220px] text-sm rounded border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-secondary px-2 py-1"
                onClick={() => {
                  setCellDialog(null);
                  setDraftValue('');
                }}
              >
                Avbryt
              </button>
              <button
                type="button"
                className="btn btn-success px-2 py-1"
                onClick={() => saveCell(cellDialog.weekNumber, cellDialog.columnKey, cellDialog.previousValue)}
                disabled={savingCell === `${cellDialog.weekNumber}:${cellDialog.columnKey}`}
              >
                Spara
              </button>
            </div>
          </div>
        </div>
      )}

      {weekDialog && grid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-4xl rounded border border-gray-200 bg-white p-3 space-y-3">
            <h2 className="text-base font-semibold">Redigera vecka {weekDialog.weekNumber}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-auto pr-1">
              {grid.columns
                .filter((c) => c.columnType === 'TEXT')
                .map((col) => (
                  <label key={col.key} className="text-sm block">
                    {col.header}
                    <textarea
                      className="mt-1 w-full min-h-[96px] text-sm rounded border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                      value={weekDialog.fields[col.key] ?? ''}
                      onChange={(e) =>
                        setWeekDialog((prev) =>
                          prev
                            ? { ...prev, fields: { ...prev.fields, [col.key]: e.target.value } }
                            : prev
                        )
                      }
                    />
                  </label>
                ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-secondary px-2 py-1"
                onClick={() => setWeekDialog(null)}
              >
                Avbryt
              </button>
              <button
                type="button"
                className="btn btn-success px-2 py-1"
                onClick={saveWeekDialog}
                disabled={savingWeekDialog}
              >
                {savingWeekDialog ? 'Sparar...' : 'Spara vecka'}
              </button>
            </div>
          </div>
        </div>
      )}

      {taskDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-lg rounded border border-gray-200 bg-white p-3 space-y-3">
            <h2 className="text-base font-semibold">
              {taskDialog.mode === 'create' ? 'Nytt ärende' : 'Redigera ärende'} • Vecka {taskDialog.weekNumber}
            </h2>
            <label className="text-sm block">
              Ärende
              <input
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                value={taskDialog.name}
                onChange={(e) => setTaskDialog((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
              />
            </label>
            <label className="text-sm block">
              Klart datum
              <input
                type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD"
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                value={taskDialog.dueDate}
                onChange={(e) => setTaskDialog((prev) => (prev ? { ...prev, dueDate: e.target.value } : prev))}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setTaskDialog(null)}>
                Avbryt
              </button>
              <button
                type="button"
                className="btn btn-success px-2 py-1"
                onClick={saveTaskDialog}
                disabled={savingTaskDialog}
              >
                {savingTaskDialog ? 'Sparar...' : 'Spara'}
              </button>
            </div>
          </div>
        </div>
      )}

      {copyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded border border-gray-200 bg-white p-3 space-y-3">
            <h2 className="text-base font-semibold">Kopiera år</h2>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-sm">Från år
                <select className="mt-1 w-full" value={copyFromYear} onChange={(e) => setCopyFromYear(Number(e.target.value))}>
                  {Array.from(new Set([copyFromYear, ...years])).sort((a, b) => b - a).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm">Till år
                <input className="mt-1 w-full" type="number" min={2000} max={2100} value={copyToYear} onChange={(e) => setCopyToYear(Number(e.target.value))} />
              </label>
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium">Kolumner att kopiera</div>
              {grid?.columns.filter((c) => c.columnType === 'TEXT' || c.key === 'tasks').map((c) => (
                <label key={c.key} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={copyColumns[c.key] ?? false} onChange={(e) => setCopyColumns((prev) => ({ ...prev, [c.key]: e.target.checked }))} />
                  {c.header}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setCopyOpen(false)}>Stäng</button>
              <button type="button" className="btn btn-success px-2 py-1" disabled={copying} onClick={handleCopyYear}>
                {copying ? 'Kopierar...' : 'Kopiera'}
              </button>
            </div>
          </div>
        </div>
      )}

      {movingTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-sm rounded border border-gray-200 bg-white p-3 space-y-3">
            <h2 className="text-base font-semibold">Flytta ärende</h2>
            <label className="text-sm block">
              Vecka
              <select className="mt-1 w-full" value={movingTask.toWeek} onChange={(e) => setMovingTask((prev) => prev ? ({ ...prev, toWeek: Number(e.target.value) }) : prev)}>
                {(grid?.rows ?? []).map((r) => (
                  <option key={r.weekNumber} value={r.weekNumber}>Vecka {r.weekNumber}</option>
                ))}
              </select>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setMovingTask(null)}>Stäng</button>
              <button type="button" className="btn btn-primary px-2 py-1" onClick={moveTask} disabled={movingBusy}>
                {movingBusy ? 'Sparar...' : 'Flytta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
